import React, { useState, useMemo } from 'react';
import { addDoc, collection, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';

const ScheduleAppointmentModal = ({ 
  isOpen, 
  onClose, 
  friend, 
  appointments = [], 
  onAppointmentAdded,
  onAppointmentDeleted 
}) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [newAppointment, setNewAppointment] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    notes: '',
    type: 'meeting'
  });

  const getAppointmentTypeLabel = (type) => {
    const typeMap = {
      'meeting': t('appointmentTypeMeeting'),
      'call': t('appointmentTypeCall'),
      'video': t('appointmentTypeVideo'),
      'lunch': t('appointmentTypeLunch'),
      'dinner': t('appointmentTypeDinner'),
      'coffee': t('appointmentTypeCoffee'),
      'activity': t('appointmentTypeActivity'),
      'other': t('appointmentTypeOther')
    };
    return typeMap[type] || type.replace('-', ' ');
  };

  // Use useMemo to recalculate when friend or appointments change
  const upcomingAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0 || !friend || !friend.id) {
      return [];
    }
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    const filtered = appointments
      .filter(apt => {
        // Use String() to ensure comparison works even if one is string and one is number
        const aptFriendId = String(apt.friendId || '');
        const friendId = String(friend.id || '');
        
        if (!aptFriendId || !friendId || aptFriendId !== friendId) return false;
        if (apt.status !== 'scheduled') return false;
        
        const aptDate = new Date(apt.date);
        if (isNaN(aptDate.getTime())) return false;
        aptDate.setHours(0, 0, 0, 0);
        return aptDate >= now;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return filtered;
  }, [appointments, friend]); // Dependencies: appointments array and friend object

  const scheduleAppointment = async (e) => {
    e.preventDefault();
    if (!friend || !newAppointment.title.trim()) return;

    try {
      const appointmentData = {
        ...newAppointment,
        friendId: friend.id,
        friendName: friend.name,
        userId: currentUser.uid,
        status: 'scheduled',
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'appointments'), appointmentData);
      
      setNewAppointment({
        title: '',
        date: '',
        time: '',
        location: '',
        notes: '',
        type: 'meeting'
      });
      
      if (onAppointmentAdded) {
        onAppointmentAdded({ id: docRef.id, ...appointmentData });
      }
      
      onClose();
    } catch (error) {
      console.error('Error scheduling appointment:', error);
    }
  };

  const deleteAppointment = async (appointmentId) => {
    if (window.confirm(t('confirmDeleteAppointment'))) {
      try {
        await deleteDoc(doc(db, 'appointments', appointmentId));
        if (onAppointmentDeleted) {
          onAppointmentDeleted(appointmentId);
        }
      } catch (error) {
        console.error('Error deleting appointment:', error);
      }
    }
  };

  const handleClose = () => {
    setNewAppointment({
      title: '',
      date: '',
      time: '',
      location: '',
      notes: '',
      type: 'meeting'
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="add-friend-modal"
      onClick={(e) => {
        if (e.target.classList.contains('add-friend-modal')) {
          handleClose();
        }
      }}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h3>{t('scheduleAppointmentTitle')}</h3>
        
        {/* Upcoming Appointments */}
        {upcomingAppointments.length > 0 && (
          <div className="existing-goals" style={{ marginBottom: '30px' }}>
            <h4>{t('upcomingAppointments')}</h4>
            {upcomingAppointments.map((appointment) => {
              const appointmentDate = new Date(appointment.date);
              const formattedDate = appointmentDate.toLocaleDateString('en-US', { 
                weekday: 'short',
                month: 'short', 
                day: 'numeric',
                year: 'numeric'
              });
              const formattedTime = appointment.time ? (() => {
                const [hours, minutes] = appointment.time.split(':');
                const hour = parseInt(hours, 10);
                const ampm = hour >= 12 ? 'PM' : 'AM';
                const displayHour = hour % 12 || 12;
                return `${displayHour}:${minutes} ${ampm}`;
              })() : '';
              
              return (
                <div key={appointment.id} className="goal-item" style={{ 
                  background: '#f8f9fa', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '12px', 
                  padding: '15px', 
                  marginBottom: '15px' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <h5 style={{ margin: '0 0 5px 0', color: '#2D3748' }}>{appointment.title}</h5>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
                        <span style={{ 
                          fontSize: '0.8rem', 
                          color: '#6B7280', 
                          background: '#f3f4f6', 
                          padding: '2px 8px', 
                          borderRadius: '12px',
                          textTransform: 'capitalize'
                        }}>
                          {getAppointmentTypeLabel(appointment.type)}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                          📅 {formattedDate}
                        </span>
                        {formattedTime && (
                          <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                            🕐 {formattedTime}
                          </span>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={() => deleteAppointment(appointment.id)}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#EF4444', 
                        cursor: 'pointer',
                        fontSize: '1.2rem'
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                  {appointment.location && (
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#6B7280' }}>
                      📍 {appointment.location}
                    </p>
                  )}
                  {appointment.notes && (
                    <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', color: '#6B7280' }}>
                      {appointment.notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <form onSubmit={scheduleAppointment}>
          <div className="form-group">
            <label>{t('friendLabel')}</label>
            <input
              type="text"
              value={friend?.name || ''}
              disabled
              className="disabled-input"
            />
          </div>

          <div className="form-group">
            <label>{t('titleLabel')}</label>
            <input
              type="text"
              value={newAppointment.title}
              onChange={(e) => setNewAppointment({...newAppointment, title: e.target.value})}
              placeholder={t('titlePlaceholder')}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('dateLabel')}</label>
              <input
                type="date"
                value={newAppointment.date}
                onChange={(e) => setNewAppointment({...newAppointment, date: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('timeLabel')}</label>
              <input
                type="time"
                value={newAppointment.time}
                onChange={(e) => setNewAppointment({...newAppointment, time: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('typeLabel')}</label>
              <select
                value={newAppointment.type}
                onChange={(e) => setNewAppointment({...newAppointment, type: e.target.value})}
              >
                <option value="meeting">{t('appointmentTypeMeeting')}</option>
                <option value="call">{t('appointmentTypeCall')}</option>
                <option value="video">{t('appointmentTypeVideo')}</option>
                <option value="lunch">{t('appointmentTypeLunch')}</option>
                <option value="dinner">{t('appointmentTypeDinner')}</option>
                <option value="coffee">{t('appointmentTypeCoffee')}</option>
                <option value="activity">{t('appointmentTypeActivity')}</option>
                <option value="other">{t('appointmentTypeOther')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('locationLabel')}</label>
              <input
                type="text"
                value={newAppointment.location}
                onChange={(e) => setNewAppointment({...newAppointment, location: e.target.value})}
                placeholder={t('locationPlaceholder')}
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('notesLabel')}</label>
            <textarea
              value={newAppointment.notes}
              onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
              placeholder={t('notesPlaceholderAppointment')}
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleClose}>
              {t('cancel')}
            </button>
            <button type="submit">{t('scheduleAppointment')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleAppointmentModal;

